import { TARGET_SCOPES, TARGET_SCOPE_LABELS, type TargetScope } from "@vital/shared";

type Site = { id: string; code: string; name: string };
type SiteGroup = {
  id: string;
  name: string;
  members: Array<{ siteId: string; siteCode: string; siteName: string }>;
};

/** Optional label overrides — defaults preserve pre-L4A wording for other composers. */
export type TargetFieldsLabels = {
  legend: string;
  scope: string;
  site: string;
  group: string;
  selectGroup: string;
  groupEmptyHint: string;
  globalHint: string;
  priority: string;
};

const DEFAULT_LABELS: TargetFieldsLabels = {
  legend: "هدف‌گذاری",
  scope: "دامنه",
  site: "سایت هدف",
  group: "گروه سایت",
  selectGroup: "انتخاب گروه…",
  groupEmptyHint: "ابتدا در «سایت‌ها و گروه‌ها» یک گروه بسازید.",
  globalHint: "برای همهٔ سایت‌های فعال نمایش داده می‌شود.",
  priority: "اولویت (۰–۱۰۰۰)",
};

type Props = {
  sites: Site[];
  groups: SiteGroup[];
  targetScope: TargetScope;
  siteCode: string;
  groupId: string;
  priority: number;
  onTargetScopeChange: (scope: TargetScope) => void;
  onSiteCodeChange: (code: string) => void;
  onGroupIdChange: (id: string) => void;
  onPriorityChange: (priority: number) => void;
  labels?: Partial<TargetFieldsLabels>;
  compact?: boolean;
};

export function TargetFields({
  sites,
  groups,
  targetScope,
  siteCode,
  groupId,
  priority,
  onTargetScopeChange,
  onSiteCodeChange,
  onGroupIdChange,
  onPriorityChange,
  labels: labelOverrides,
  compact = false,
}: Props) {
  const labels = { ...DEFAULT_LABELS, ...labelOverrides };
  const targetControl =
    targetScope === "site" ? (
      <label>
        <span>{labels.site}</span>
        <select value={siteCode} onChange={(e) => onSiteCodeChange(e.target.value)}>
          {sites.map((s) => (
            <option key={s.id} value={s.code}>
              {s.name} ({s.code})
            </option>
          ))}
        </select>
      </label>
    ) : targetScope === "site_group" ? (
      <label>
        <span>{labels.group}</span>
        <select value={groupId} onChange={(e) => onGroupIdChange(e.target.value)} required>
          <option value="">{labels.selectGroup}</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name} ({g.members.length} سایت)
            </option>
          ))}
        </select>
        {groups.length === 0 && labels.groupEmptyHint ? (
          <p className="field-hint">{labels.groupEmptyHint}</p>
        ) : null}
      </label>
    ) : labels.globalHint ? (
      <p className="field-hint field-hint--block">{labels.globalHint}</p>
    ) : null;

  return (
    <fieldset className={`schedule-fieldset${compact ? " schedule-fieldset--compact" : ""}`}>
      <legend>{labels.legend}</legend>
      <div className={compact ? "composer-fields-row composer-fields-row--3" : undefined}>
        <label>
          <span>{labels.scope}</span>
          <select
            value={targetScope}
            onChange={(e) => onTargetScopeChange(e.target.value as TargetScope)}
          >
            {TARGET_SCOPES.map((scope) => (
              <option key={scope} value={scope}>
                {TARGET_SCOPE_LABELS[scope]}
              </option>
            ))}
          </select>
        </label>
        {targetControl}
        <label>
          <span>{labels.priority}</span>
          <input
            type="number"
            dir="ltr"
            className="ltr-isolate"
            min={0}
            max={1000}
            value={priority}
            onChange={(e) => onPriorityChange(Number(e.target.value))}
          />
        </label>
      </div>
    </fieldset>
  );
}

export function targetingPayload(
  targetScope: TargetScope,
  siteCode: string,
  groupId: string,
  priority: number,
) {
  return {
    targetScope,
    siteCode: targetScope === "site" ? siteCode : undefined,
    groupId: targetScope === "site_group" ? groupId : undefined,
    priority,
  };
}
export function formatTargetLabel(item: {
  scope: string;
  siteCode: string | null;
  siteName: string | null;
  groupId: string | null;
  groupName: string | null;
}): string {
  if (item.scope === "global") return TARGET_SCOPE_LABELS.global;
  if (item.scope === "site_group") {
    return `${TARGET_SCOPE_LABELS.site_group}: ${item.groupName ?? item.groupId ?? "—"}`;
  }
  return `${TARGET_SCOPE_LABELS.site}: ${item.siteName ?? item.siteCode ?? "—"}`;
}
